// src/components/unit-list/ctl-unit-list.js
//
// ctl-unit-list — thin, tenant-friendly product/unit listing orchestrator.
//
// Attributes
// - view: "grid" | "list" (default "grid")
// - loading: boolean
// - error: boolean
// - infinite: boolean                 // enable auto load-more when sentinel enters view
// - cursor: string (optional)         // passed through in ctl-load-more
// - page-size: number (optional)      // hint only
// - aria-label: string (optional)
//
// Properties
// - items: array                      // [{ id, ... }] (required id)
// - total: number|null                // optional total results count
// - view: "grid"|"list"
//
// Events
// - ctl-item-click  detail: { id, item }
// - ctl-item-cta    detail: { id, action, item }  // action="reserve"|"details"|...
// - ctl-load-more   detail: { cursor, pageSize }
// - ctl-view-change detail: { view }
//
// Slots
// - toolbar    : filters/sort/header actions
// - empty      : empty state (when !loading && !error && items.length===0)
// - error      : error state (when error)
// - item       : <template slot="item"> custom renderer for each item
// - footer     : after list (e.g., legal notes)
//
// Template slot="item" variables
// - {{id}} {{name}} {{size}} {{type}} {{price}} {{priceText}} {{location}} {{image}} {{selected}}
// - {{item}} JSON string for advanced usage
//
// Notes
// - By default renders <ctl-unit-card> per item if present (recommended).
// - Infinite loading emits ctl-load-more when sentinel becomes visible.
// - Includes a lightweight “results header” with optional total.
// - Exposes parts for theming: frame, header, toolbar, list, item, sentinel, empty, error, footer.

import "./ctl-unit-card.js";
import "../foundation/ctl-skeleton.js";
import "../foundation/ctl-spinner.js";
import "../foundation/ctl-button.js";
import "../data-display/ctl-empty-state.js";
import "../foundation/ctl-divider.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-text: var(--ctl-color-text);
    --ctl-muted: var(--ctl-color-muted);
    --ctl-bg: var(--ctl-color-bg);
    --ctl-surface: var(--ctl-color-surface);
    --ctl-border: var(--ctl-color-border);
    --ctl-radius: var(--ctl-radius-lg, 14px);

    --ctl-gap: 12px;
    --ctl-grid-min: 260px; /* min card width for auto-fill */
    --ctl-focus: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-text);
  }

  .frame{
    display:grid;
    gap: var(--ctl-space-2);
  }

  .header{
    display:flex;
    align-items:flex-end;
    justify-content:space-between;
    gap: var(--ctl-space-3);
    flex-wrap: wrap;
  }

  .meta{
    display:grid;
    gap: var(--ctl-space-0-5);
    min-width: 0;
  }

  .title{
    font-weight: var(--ctl-font-weight-ultra);
    letter-spacing: -0.01em;
    line-height: var(--line-height-tight, 1.1);
  }

  .sub{
    color: var(--ctl-muted);
    font-size: var(--font-size-xs, 0.75rem);
    font-weight: var(--ctl-font-weight-heavy);
  }

  .toolbar{
    display:flex;
    align-items:center;
    justify-content:flex-end;
    gap: var(--spacing-xs, 8px);
    flex-wrap: wrap;
  }
  .toolbar:empty{ display:none; }

  .list{
    display:grid;
    gap: var(--ctl-gap);
  }

  :host([view="grid"]) .list{
    grid-template-columns: repeat(auto-fill, minmax(var(--ctl-grid-min), 1fr));
    align-items: stretch;
  }

  :host([view="list"]) .list{
    grid-template-columns: 1fr;
  }

  .item{
    outline: none;
  }

  .item:focus-visible{
    box-shadow: var(--ctl-focus);
    border-radius: var(--ctl-radius);
  }

  /* States */
  .state{
    border: var(--border-width-thin, 1px) solid var(--ctl-border);
    background: color-mix(in srgb, var(--ctl-border) 10%, var(--ctl-bg));
    border-radius: var(--ctl-radius);
    padding: var(--spacing-md, 16px);
    color: var(--ctl-muted);
  }

  .state h3{
    margin: 0 0 var(--spacing-xs, 6px) 0;
    color: var(--ctl-text);
    font-size: var(--font-size-sm, 0.875rem);
    font-weight: var(--ctl-font-weight-ultra);
    letter-spacing: -0.01em;
  }

  .state p{
    margin: 0 0 var(--spacing-xs, 10px) 0;
    font-size: var(--font-size-xs, 0.75rem);
    line-height: var(--line-height-normal, 1.45);
  }

  .stateActions{
    display:flex;
    gap: var(--spacing-xs, 10px);
    flex-wrap: wrap;
    align-items:center;
  }

  /* Loading skeleton grid */
  .skGrid{
    display:grid;
    gap: var(--ctl-gap);
  }
  :host([view="grid"]) .skGrid{
    grid-template-columns: repeat(auto-fill, minmax(var(--ctl-grid-min), 1fr));
  }
  :host([view="list"]) .skGrid{
    grid-template-columns: 1fr;
  }
  .skCard{
    border: var(--border-width-thin, 1px) solid var(--ctl-border);
    background: var(--ctl-surface);
    border-radius: var(--ctl-radius);
    overflow:hidden;
  }
  .skMedia{ padding: var(--spacing-sm, 12px); border-bottom: var(--border-width-thin, 1px) solid var(--ctl-border); }
  .skBody{ padding: var(--spacing-sm, 12px); display:grid; gap: var(--spacing-xs, 10px); }

  .sentinel{
    display:flex;
    align-items:center;
    justify-content:center;
    padding: var(--spacing-xs, 10px);
    color: var(--ctl-muted);
  }

  .footer{
    color: var(--ctl-muted);
    font-size: var(--font-size-xs, 0.75rem);
  }
  .footer:empty{ display:none; }

  ::slotted(template[slot="item"]){ display:none; }
</style>

<div class="frame" part="frame">
  <div class="header" part="header">
    <div class="meta" part="meta">
      <div class="title" part="title"><slot name="title">Available units</slot></div>
      <div class="sub" part="sub"><span id="subText"></span></div>
    </div>

    <div class="toolbar" part="toolbar">
      <slot name="toolbar"></slot>
    </div>
  </div>

  <ctl-divider part="divider"></ctl-divider>

  <div id="errorState" class="state" part="error" hidden>
    <h3>Something went wrong</h3>
    <p>We couldn’t load results. Try again.</p>
    <div class="stateActions">
      <ctl-button id="retryBtn" variant="secondary">Retry</ctl-button>
      <slot name="error"></slot>
    </div>
  </div>

  <div id="emptyState" class="state" part="empty" hidden>
    <slot name="empty">
      <ctl-empty-state>
        <div slot="title">No matching units</div>
        <div slot="message">Try adjusting your filters.</div>
      </ctl-empty-state>
    </slot>
  </div>

  <div id="loadingState" part="loading" hidden>
    <div class="skGrid" part="skeleton">
      <div class="skCard" part="skeleton-item">
        <div class="skMedia">
          <ctl-skeleton variant="rect" height="120px"></ctl-skeleton>
        </div>
        <div class="skBody">
          <ctl-skeleton variant="text" lines="2"></ctl-skeleton>
          <ctl-skeleton variant="text" lines="1" width="70%"></ctl-skeleton>
          <ctl-skeleton variant="rect" height="36px"></ctl-skeleton>
        </div>
      </div>
      <div class="skCard" part="skeleton-item">
        <div class="skMedia">
          <ctl-skeleton variant="rect" height="120px"></ctl-skeleton>
        </div>
        <div class="skBody">
          <ctl-skeleton variant="text" lines="2"></ctl-skeleton>
          <ctl-skeleton variant="text" lines="1" width="70%"></ctl-skeleton>
          <ctl-skeleton variant="rect" height="36px"></ctl-skeleton>
        </div>
      </div>
      <div class="skCard" part="skeleton-item">
        <div class="skMedia">
          <ctl-skeleton variant="rect" height="120px"></ctl-skeleton>
        </div>
        <div class="skBody">
          <ctl-skeleton variant="text" lines="2"></ctl-skeleton>
          <ctl-skeleton variant="text" lines="1" width="70%"></ctl-skeleton>
          <ctl-skeleton variant="rect" height="36px"></ctl-skeleton>
        </div>
      </div>
    </div>
  </div>

  <div id="list" class="list" part="list" hidden></div>

  <div id="sentinel" class="sentinel" part="sentinel" hidden>
    <ctl-spinner size="sm" label="Loading more"></ctl-spinner>
  </div>

  <div class="footer" part="footer">
    <slot name="footer"></slot>
  </div>

  <slot id="itemSlot" name="item"></slot>
</div>
`;

function toArray(v){ return Array.isArray(v) ? v : []; }
function esc(s){
  return String(s ?? "")
    .replaceAll("&","&amp;")
    .replaceAll("<","&lt;")
    .replaceAll(">","&gt;")
    .replaceAll('"',"&quot;")
    .replaceAll("'","&#039;");
}

function safeJsonParse(str, fallback){
  try { return JSON.parse(str); } catch { return fallback; }
}

function normalizeView(v){
  const s = String(v || "grid");
  return (s === "list" || s === "grid") ? s : "grid";
}

function moneyLike(v){
  // permissive formatting helper for template variables
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export class CtlUnitList extends HTMLElement {
  static get observedAttributes(){
    return ["view","loading","error","cursor","page-size","infinite"];
  }

  constructor(){
    super();
    this.attachShadow({ mode:"open" }).appendChild(template.content.cloneNode(true));

    this._subText = this.shadowRoot.getElementById("subText");

    this._errorState = this.shadowRoot.getElementById("errorState");
    this._emptyState = this.shadowRoot.getElementById("emptyState");
    this._loadingState = this.shadowRoot.getElementById("loadingState");
    this._list = this.shadowRoot.getElementById("list");
    this._sentinel = this.shadowRoot.getElementById("sentinel");

    this._retryBtn = this.shadowRoot.getElementById("retryBtn");
    this._itemSlot = this.shadowRoot.getElementById("itemSlot");

    this._items = [];
    this._total = null;

    this._itemTemplate = null;

    this._io = null;
    this._loadingMore = false;

    this._onClick = this._onClick.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
    this._onRetryClick = this._onRetryClick.bind(this);
  }

  connectedCallback(){
    if (!this.hasAttribute("view")) this.setAttribute("view", "grid");

    // (Optional) allow items via attribute for simple demos, but prefer property assignment
    if (this.hasAttribute("items")) {
      this.items = safeJsonParse(this.getAttribute("items"), []);
    }

    this.shadowRoot.addEventListener("click", this._onClick);
    this._itemSlot.addEventListener("slotchange", this._onSlotChange);
    this._retryBtn.addEventListener("click", this._onRetryClick);

    this._collectTemplate();
    this._setupIO();
    this._render();
  }

  disconnectedCallback(){
    this.shadowRoot.removeEventListener("click", this._onClick);
    this._itemSlot.removeEventListener("slotchange", this._onSlotChange);
    this._retryBtn.removeEventListener("click", this._onRetryClick);
    this._io?.disconnect?.();
    this._io = null;
    
    // Clear any pending timeout to prevent memory leaks
    if (this._loadingTimeout) {
      clearTimeout(this._loadingTimeout);
      this._loadingTimeout = null;
    }
  }

  attributeChangedCallback(name){
    if (name === "view") {
      const v = normalizeView(this.getAttribute("view"));
      this.setAttribute("view", v);
      this.dispatchEvent(new CustomEvent("ctl-view-change", {
        bubbles: true,
        composed: true,
        detail: { view: v }
      }));
      this._render();
      return;
    }

    if (name === "loading" || name === "error" || name === "infinite" || name === "cursor" || name === "page-size") {
      this._setupIO();
      this._render();
    }
  }

  // ---- API
  get view(){ return normalizeView(this.getAttribute("view")); }
  set view(v){ this.setAttribute("view", normalizeView(v)); }

  get loading(){ return this.hasAttribute("loading"); }
  set loading(v){ v ? this.setAttribute("loading","") : this.removeAttribute("loading"); }

  get error(){ return this.hasAttribute("error"); }
  set error(v){ v ? this.setAttribute("error","") : this.removeAttribute("error"); }

  get infinite(){ return this.hasAttribute("infinite"); }
  set infinite(v){ v ? this.setAttribute("infinite","") : this.removeAttribute("infinite"); }

  get cursor(){ return this.getAttribute("cursor") || ""; }
  set cursor(v){ v ? this.setAttribute("cursor", String(v)) : this.removeAttribute("cursor"); }

  get pageSize(){
    const n = Number(this.getAttribute("page-size") || "");
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  set pageSize(v){
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) this.setAttribute("page-size", String(Math.floor(n)));
    else this.removeAttribute("page-size");
  }

  get items(){ return this._items; }
  set items(v){
    this._items = toArray(v).map((it) => {
      const o = (it && typeof it === "object") ? it : {};
      return { ...o, id: String(o.id ?? "") };
    }).filter((it) => it.id);

    // Optional: reflect for simple HTML demos (comment out if you don’t want big attrs)
    // this.setAttribute("items", JSON.stringify(this._items));

    this._render();
  }

  get total(){ return this._total; }
  set total(v){
    const n = Number(v);
    this._total = Number.isFinite(n) ? n : null;
    this._renderHeader();
  }

  // ---- templating
  _onSlotChange(){
    this._collectTemplate();
    this._render();
  }

  _collectTemplate(){
    const assigned = this._itemSlot.assignedElements({ flatten: true }) || [];
    this._itemTemplate = assigned.find((el) => el.tagName === "TEMPLATE") || null;
  }

  _renderTemplate(tmpl, data){
    return tmpl.innerHTML.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
      const v = data[key];
      return esc(v == null ? "" : String(v));
    });
  }

  // ---- infinite load
  _setupIO(){
    this._io?.disconnect?.();
    this._io = null;

    if (!this.infinite) return;
    if (this.loading || this.error) return;

    this._io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry || !entry.isIntersecting) return;

      // Prevent spamming while host is fetching
      if (this._loadingMore) return;
      if (this.loading) return;
      if (this.error) return;

      this._loadingMore = true;
      this._sentinel.hidden = false;

      this.dispatchEvent(new CustomEvent("ctl-load-more", {
        bubbles: true,
        composed: true,
        detail: { cursor: this.cursor || null, pageSize: this.pageSize }
      }));

      // Host should clear _loadingMore by toggling loading attr off/on;
      // but we’ll also self-reset after a short delay as a safety.
      // Store timeout reference for cleanup (cleared in disconnectedCallback only)
      this._loadingTimeout = setTimeout(() => { this._loadingMore = false; this._loadingTimeout = null; }, 1200);
    }, { root: null, rootMargin: "600px 0px 600px 0px", threshold: 0.01 });

    this._io.observe(this._sentinel);
  }

  // ---- rendering
  _render(){
    this._renderHeader();

    const items = this._items;
    const isError = this.error;
    const isLoading = this.loading;
    const isEmpty = !isLoading && !isError && items.length === 0;

    // states
    this._errorState.hidden = !isError;
    this._loadingState.hidden = !isLoading;
    this._emptyState.hidden = !isEmpty;

    // list visibility
    this._list.hidden = isLoading || isError || isEmpty;

    // sentinel visibility
    const showSentinel = this.infinite && !isLoading && !isError && items.length > 0;
    this._sentinel.hidden = !showSentinel;

    if (!this._list.hidden) this._renderItems();

    this._setupIO();
  }

  _renderHeader(){
    const count = this._items.length;

    // Prefer total if provided, otherwise just show visible count
    let text = "";
    if (this.error) text = "Unable to load results.";
    else if (this.loading && count === 0) text = "Loading results…";
    else if (this._total != null) text = `${count} of ${this._total} shown`;
    else text = `${count} result${count === 1 ? "" : "s"}`;

    this._subText.textContent = text;
  }

  _renderItems(){
    const items = this._items;

    const html = items.map((it) => {
      const data = {
        id: it.id,
        name: it.name ?? it.title ?? "",
        size: it.size ?? "",
        type: it.type ?? "",
        location: it.location ?? "",
        price: it.price ?? "",
        priceText: it.priceText ?? moneyLike(it.price),
        image: it.image ?? it.src ?? "",
        selected: "false",
        item: JSON.stringify(it)
      };

      const inner = this._itemTemplate
        ? this._renderTemplate(this._itemTemplate, data)
        : `
          <ctl-unit-card
            data-role="item"
            data-id="${esc(it.id)}"
            unit-id="${esc(it.id)}"
            name="${esc(data.name)}"
            size="${esc(data.size)}"
            type="${esc(data.type)}"
            location="${esc(data.location)}"
            price="${esc(String(data.price ?? ""))}"
          ></ctl-unit-card>
        `;

      return `
        <div class="item" part="item" data-role="wrap" data-id="${esc(it.id)}" tabindex="0">
          ${inner}
        </div>
      `;
    }).join("");

    this._list.innerHTML = html;
  }

  // ---- interaction
  _onClick(e){
    const path = e.composedPath ? e.composedPath() : [];
    const actionEl = path.find((n) => n?.dataset?.action);
    if (actionEl) {
      const wrap = path.find((n) => n?.dataset?.role === "wrap" && n?.dataset?.id);
      if (!wrap) return;

      const id = wrap.dataset.id;
      const item = this._items.find((x) => x.id === id) || null;
      const action = String(actionEl.dataset.action || "");

      this.dispatchEvent(new CustomEvent("ctl-item-cta", {
        bubbles: true,
        composed: true,
        detail: { id, action, item }
      }));
      return;
    }

    // item click
    const wrap = path.find((n) => n?.dataset?.role === "wrap" && n?.dataset?.id);
    if (wrap) {
      const id = wrap.dataset.id;
      const item = this._items.find((x) => x.id === id) || null;

      this.dispatchEvent(new CustomEvent("ctl-item-click", {
        bubbles: true,
        composed: true,
        detail: { id, item }
      }));
    }
  }

  _onRetryClick() {
    this.dispatchEvent(new CustomEvent("ctl-load-more", {
      bubbles: true,
      composed: true,
      detail: { cursor: this.cursor || null, pageSize: this.pageSize }
    }));
  }
}

customElements.define("ctl-unit-list", CtlUnitList);
